import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import type { GenericSearchResult } from '@/features/SearchPage/types';
import type { SearchResponse } from '@music-vine/setlist';

export interface SearchResults {
  results: SearchResponse<GenericSearchResult>[];
  searchTerm: string;
}

export const searchResultsAtom = atom<SearchResults | undefined>(undefined);

// Custom storage handler for cachedSearchResults
const searchStorageHandler = {
  // Add a custom storage handler to limit cache size
  getItem: (key: string, initialValue: Record<string, SearchResults>) => {
    try {
      const storedValue = localStorage.getItem(key);

      return storedValue
        ? (JSON.parse(storedValue) as Record<string, SearchResults>)
        : initialValue;
    } catch (error) {
      console.warn('Error retrieving from localStorage:', error);

      // Clear the invalid data
      try {
        localStorage.removeItem(key);
      } catch {
        // Ignore cleanup errors
      }

      return initialValue;
    }
  },

  setItem: (key: string, value: Record<string, SearchResults>) => {
    try {
      // More aggressive limit - from 50 to 20 items max
      const valueToStore = { ...value };
      const keys = Object.keys(valueToStore);

      // If we have more than 20 items, remove the oldest ones
      if (keys.length > 20) {
        // Sort keys by most recently added (assuming most recent is at the end)
        const keysToRemove = keys.slice(0, keys.length - 20);

        keysToRemove.forEach((k) => {
          delete valueToStore[k];
        });
      }

      // Implement size limiting for each cached result - fix iterator issue
      Object.keys(valueToStore).forEach((k) => {
        const result = valueToStore[k];

        // Use optional chaining for better readability
        if (result?.results) {
          // Limit the number of hits to reduce storage size
          result.results = result.results.map((category) => {
            if (
              category.hits &&
              Array.isArray(category.hits) &&
              category.hits.length > 10
            ) {
              // Only keep the top 10 hits in each category
              return {
                ...category,
                hits: category.hits.slice(0, 10),
              };
            }

            return category;
          });
        }
      });

      // Try to store - catch quota errors
      const serialized = JSON.stringify(valueToStore);

      // Check size before attempting to store (rough estimation)
      if (serialized.length > 2_000_000) {
        // ~2MB limit
        console.warn('Cache too large, pruning older entries');
        // If the serialized data is too large, keep only the 5 most recent entries
        const prunedValue: Record<string, SearchResults> = {};
        const recentKeys = keys.slice(-5);

        recentKeys.forEach((k) => {
          prunedValue[k] = valueToStore[k];
        });
        localStorage.setItem(key, JSON.stringify(prunedValue));
      } else {
        localStorage.setItem(key, serialized);
      }
    } catch (error) {
      console.warn('Error storing to localStorage:', error);

      // If we hit a quota error, clear the entire cache and try again with just the new item
      if (
        error instanceof DOMException &&
        (error.name === 'QuotaExceededError' || error.code === 22)
      ) {
        try {
          // Clear all storage related to search first
          localStorage.removeItem(key);

          // Keep only the most recent search result if possible
          const keys = Object.keys(value);

          if (keys.length > 0) {
            const latestKey = keys[keys.length - 1];
            const newEntry = { [latestKey]: value[latestKey] };

            // Further limit the result size if needed - use optional chaining
            if (newEntry[latestKey]?.results) {
              newEntry[latestKey].results = newEntry[latestKey].results.map(
                (category) => {
                  if (
                    category.hits &&
                    Array.isArray(category.hits) &&
                    category.hits.length > 5
                  ) {
                    return {
                      ...category,
                      hits: category.hits.slice(0, 5),
                    };
                  }

                  return category;
                }
              );
            }

            localStorage.setItem(key, JSON.stringify(newEntry));
          }
        } catch (retryError) {
          console.error(
            'Failed to store even after clearing cache:',
            retryError
          );

          // As a last resort, clear all localStorage
          try {
            // Only clear our app-specific items to be polite
            const storageLength = localStorage.length;

            for (let i = 0; i < storageLength; i += 1) {
              const storageKey = localStorage.key(i);

              // Use optional chaining
              if (storageKey?.includes('search')) {
                localStorage.removeItem(storageKey);
              }
            }
          } catch {
            // Last attempt failed, we can't do anything more
          }
        }
      }
    }
  },

  removeItem: (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Error removing from localStorage:', error);
    }
  },
};

export const cachedSearchResultsAtom = atomWithStorage<
  Record<string, SearchResults>
>('searchResults', {}, searchStorageHandler);

// Atom to manage the search overlay state
export const searchOverlayAtom = atom<boolean>(false);
